import { useCallback, useMemo, useState } from "react";

export function getSessionStorageOrDefault<T>(key: string, defaultValue: T): T {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) {
      return defaultValue;
    }
    return JSON.parse(stored);
  } catch (e) {
    console.warn(`failed to read or parse ${key} session storage item`, e);
    return defaultValue;
  }
}

/**
 * Provide a hook for session storage.
 * @param key - the key for the item to store
 * @param defaultValue - the default initial value if the value is not set in session storage.
 */
export const useSessionStorage = <T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void, () => void] => {
  const [value, setValue] = useState(getSessionStorageOrDefault<T>(key, defaultValue));

  const setSessionStore = useCallback(
    (value: T) => {
      if (value === null || value === undefined) {
        throw new Error("Call removeSessionStorage if you want to remove this value");
      }
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
        setValue(value);
      } catch (e) {
        console.warn(`failed to set ${key} session storage item`, e);
      }
    },
    [key, setValue]
  );

  const removeSessionStore = useCallback(() => {
    try {
      sessionStorage.removeItem(key);
      setValue(defaultValue);
    } catch (e) {
      console.warn(`failed to remove ${key} session storage item`, e);
    }
  }, [key, setValue, defaultValue]);

  return useMemo(
    () => [value, setSessionStore, removeSessionStore],
    [value, setSessionStore, removeSessionStore]
  );
};
